import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import ExamListModal from '../components/WrongAnswer/ExamListModal';
import ErrorNotePrint from '../components/WrongAnswer/ErrorNotePrint';
import ErrnotePrintOverlay from '../components/WrongAnswer/ErrnotePrintOverlay';
import WorkBookPrint from '../components/WrongAnswer/WorkBookPrint';
import { useAuth } from '../context/AuthContext';
import { Printer, BookOpen, FileText, PlusCircle, Trash2, Edit2 } from 'lucide-react';
import { Button, Modal } from '../components/common';

// 툴팁 컴포넌트
const Tooltip = ({ children, content, multiline = false }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!content) return children;

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform">
          <div className={`rounded-lg bg-gray-800 px-3 py-2 text-sm text-white shadow-lg ${multiline ? 'max-w-xs whitespace-pre-wrap' : 'whitespace-nowrap'}`}>
            {content}
            <div className="absolute left-1/2 top-full -mt-1 size-0 -translate-x-1/2 transform border-4 border-transparent border-t-gray-800" />
          </div>
        </div>
      )}
    </div>
  );
};

function WrongAnswer() {
  const { student_id, stamp_id } = useParams();
  const { getAccessToken, userType } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [examList, setExamList] = useState([]);
  const [stampInfo, setStampInfo] = useState(null);
  const [wrongAnswerList, setWrongAnswerList] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [isErrorNotePrintOpen, setIsErrorNotePrintOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [printFunction, setPrintFunction] = useState(null);
  const [showV4Print, setShowV4Print] = useState(false); // 신규 인쇄(v4) 오버레이
  const [isWorkBookPreviewOpen, setIsWorkBookPreviewOpen] = useState(false);
  const [workBookPrintFunction, setWorkBookPrintFunction] = useState(null);
  const [isWorkBookModalOpen, setIsWorkBookModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [isWorkBookPreviewModalOpen, setIsWorkBookPreviewModalOpen] = useState(false);
  const [previewSettings, setPreviewSettings] = useState({
    addEmptyPage: false,
    bookId: null,
    bookAddEmptyPage: false
  });
  const [showPreviewSettings, setShowPreviewSettings] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetReviewId, setDeleteTargetReviewId] = useState(null);
  const [memoModalOpen, setMemoModalOpen] = useState(false);
  const [editingMemo, setEditingMemo] = useState({ content_id: null, content_type: 'review', memo: '' });

  const fetchExamList = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}`,
        {
          headers: { 'Authorization': token }
        }
      );

      if (response.status === 204) {
        setExamList([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExamList(data.data);
    } catch (error) {
      console.error('시험 목록 조회 오류:', error);
    }
  };

  const fetchStampInfo = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student_id}`,
        {
          headers: { 'Authorization': token }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const currentStamp = data.data.stamps.find(stamp => stamp.stamp_id === parseInt(stamp_id));
      // console.log('Current stamp info:', currentStamp);
      setStampInfo(currentStamp);
    } catch (error) {
      console.error('스탬프 정보 조회 오류:', error);
    }
  };

  const fetchWrongAnswerList = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews?stamp_id=${stamp_id}`,
        {
          headers: { 'Authorization': token }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setWrongAnswerList(data.data || []);
    } catch (error) {
      console.error('오답노트 목록 조회 오류:', error);
      setWrongAnswerList([]);
    }
  };

  useEffect(() => {
    fetchStampInfo();
    fetchExamList();
    fetchWrongAnswerList();
  }, [stamp_id]);

  useEffect(() => {
    // console.log('Current examList:', examList);
    // console.log('Current stampInfo:', stampInfo);
  }, [examList, stampInfo]);

  useEffect(() => {
    // 인쇄 시 스타일 추가
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .no-print {
          display: none !important;
        }
        .print-container {
          width: 328mm;
          margin: 0 auto;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const addWrongAnswerData = (data) => {
    fetchWrongAnswerList(); // 오답노트가 생성된 후 목록을 새로고침
  };

  // 인쇄 핸들러
  const handlePrint = useCallback(async () => {
    try {
      // 인쇄 기록 생성 API 호출
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews/print`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            content_type: 'errnote',
            content_id: selectedNoteId
          })
        }
      );

      if (!response.ok) {
        throw new Error('인쇄 기록 생성에 실패했습니다.');
      }

      // 인쇄 실행
      if (printFunction) {
        printFunction();
      }
    } catch (error) {
      console.error('인쇄 기록 생성 오류:', error);
      alert('인쇄 기록을 생성하는데 실패했습니다.');
    }
  }, [printFunction, selectedNoteId, getAccessToken]);

  // 오답노트 미리보기 핸들러 수정
  const handlePrintErrorNote = (noteId) => {
    setSelectedNoteId(noteId);
    setShowPreviewSettings(true);  // 설정 모달 먼저 표시
  };

  // 문제집 인쇄 핸들러
  const handleWorkBookPrint = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews/print`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            content_type: 'book',
            content_id: selectedBookId
          })
        }
      );

      if (!response.ok) {
        throw new Error('인쇄 기록 생성에 실패했습니다.');
      }

      if (workBookPrintFunction) {
        workBookPrintFunction();
      }
    } catch (error) {
      console.error('인쇄 기록 생성 오류:', error);
      alert('인쇄 기록을 생성하는데 실패했습니다.');
    }
  }, [workBookPrintFunction, selectedBookId, getAccessToken]);

  // 문제집 미리보기 모달 열기
  const openWorkBookPreviewModal = (item) => {
    if (!item.books || item.books.length === 0) return;
    
    setPreviewSettings({
      bookId: item.books[0].book_id,
      addEmptyPage: true
    });
    setSelectedBookId(item.books[0].book_id);
    setIsWorkBookPreviewModalOpen(true);
  };

  // 문제집 미리보기 시작
  const startWorkBookPreview = () => {
    setIsWorkBookPreviewModalOpen(false);
    setSelectedBookId(previewSettings.bookId);
    setIsWorkBookPreviewOpen(true);
  };

  // 문제집 생성 모달 열기
  const openWorkBookModal = (reviewId) => {
    setSelectedReviewId(reviewId);
    setIsWorkBookModalOpen(true);
  };

  // 문제집 생성 핸들러 수정
  const handleCreateWorkBook = async (reviewId, probsNumber) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews/book`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            review_id: reviewId,
            probs_number: probsNumber
          })
        }
      );

      if (response.status === 416) {
        const data = await response.json();
        alert(`최대 ${data.data.available_problems}개까지 문제를 생성할 수 있습니다.`);
        return;
      }

      if (response.status === 409) {
        alert('이미 문제집이 존재합니다.');
        return;
      }

      if (response.status === 423) {
        alert('틀린 문제가 없어 문제집을 생성할 수 없습니다.');
        return;
      }

      if (!response.ok) {
        throw new Error('문제집 생성에 실패했습니다.');
      }

      alert('문제집이 생성되었습니다.');
      fetchWrongAnswerList(); // 목록 새로고침
    } catch (error) {
      console.error('문제집 생성 오류:', error);
      alert('문제집 생성에 실패했습니다.');
    } finally {
      setIsWorkBookModalOpen(false);
    }
  };

  // 삭제 확인 모달 열기
  const openDeleteModal = (reviewId) => {
    setDeleteTargetReviewId(reviewId);
    setDeleteModalOpen(true);
  };

  // 삭제 핸들러
  const handleDeleteReview = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews?review_id=${deleteTargetReviewId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': token
          }
        }
      );

      if (response.status === 404) {
        alert('해당 리뷰를 찾을 수 없습니다.');
        return;
      }

      if (!response.ok) {
        throw new Error('삭제 중 오류가 발생했습니다.');
      }

      alert('성공적으로 삭제되었습니다.');
      fetchWrongAnswerList(); // 목록 새로고침
    } catch (error) {
      console.error('삭제 오류:', error);
      alert('삭제에 실패했습니다.');
    } finally {
      setDeleteModalOpen(false);
      setDeleteTargetReviewId(null);
    }
  };

  // 메모 수정 모달 열기
  const openMemoModal = (contentId, contentType, currentMemo) => {
    setEditingMemo({
      content_id: contentId,
      content_type: contentType,
      memo: currentMemo || ''
    });
    setMemoModalOpen(true);
  };

  // 메모 수정 핸들러
  const handleUpdateMemo = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            memo: editingMemo.memo,
            content_id: editingMemo.content_id,
            content_type: editingMemo.content_type
          })
        }
      );

      if (response.status === 404) {
        alert('해당 항목을 찾을 수 없습니다.');
        return;
      }

      if (!response.ok) {
        throw new Error('메모 수정 중 오류가 발생했습니다.');
      }

      alert('메모가 수정되었습니다.');
      fetchWrongAnswerList(); // 목록 새로고침
    } catch (error) {
      console.error('메모 수정 오류:', error);
      alert('메모 수정에 실패했습니다.');
    } finally {
      setMemoModalOpen(false);
      setEditingMemo({ content_id: null, content_type: 'review', memo: '' });
    }
  };

  return (
    <Layout>
      {!showPreview && !isWorkBookPreviewOpen ? (
        <>
          <div className="h-full rounded-3xl bg-white p-6 shadow">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {stampInfo?.tree_name || '교육과정'} 오답노트
                  </h2>
                  <Button
                    variant="primary"
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="size-5" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    오답노트 생성
                  </Button>
                </div>

                <div className="mt-6 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          No. 
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                          작성자
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500">
                          TID(회차)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          메모
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          오답 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          전체 문제 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          생성일
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          관리
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          삭제
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {(wrongAnswerList || []).map((item) => (
                        <tr key={item.seq}>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {item.seq}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {item.author}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <Tooltip 
                              content={
                                item.exam_ids.map((id, index) => 
                                  `${id}(${item.exam_rounds[index]})`
                                ).join(', ').length > 30 
                                ? item.exam_ids.map((id, index) => 
                                    `${id}(${item.exam_rounds[index]})`
                                  ).join(', ') 
                                : null
                              }
                            >
                              <div 
                                className="truncate"
                                style={{ maxWidth: '200px' }}
                              >
                                {item.exam_ids.map((id, index) => (
                                  <span key={id}>
                                    {id}({item.exam_rounds[index]})
                                    {index < item.exam_ids.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            </Tooltip>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <Tooltip 
                                content={item.memo && item.memo.length > 20 ? item.memo : null}
                                multiline={true}
                              >
                                <span 
                                  className="truncate" 
                                  style={{ maxWidth: '150px' }}
                                >
                                  {item.memo || '-'}
                                </span>
                              </Tooltip>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openMemoModal(item.review_id, 'review', item.memo)}
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                <Edit2 className="size-4" />
                              </Button>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {item.wrong_problems}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {item.total_problems}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                            {new Date(item.created_at).toLocaleDateString('ko-KR')}
                          </td>
                          <td className="flex space-x-2 whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handlePrintErrorNote(item.errnotes?.[0]?.note_id)}
                              className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100"
                            >
                              <FileText className="size-4" />
                              오답노트
                            </Button>
                            {userType !== 'teacher' && (
                              <>
                                {item.books && item.books.length > 0 ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => openWorkBookPreviewModal(item)}
                                    className="flex items-center gap-1 bg-purple-50 text-purple-600 hover:bg-purple-100"
                                  >
                                    <BookOpen className="size-4" />
                                    문제집
                                  </Button>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => openWorkBookModal(item.review_id)}
                                    className="flex items-center gap-1 bg-green-50 text-success hover:bg-green-100"
                                  >
                                    <PlusCircle className="size-4" />
                                    문제집 생성
                                  </Button>
                                )}
                              </>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openDeleteModal(item.review_id)}
                              className="flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-100"
                            >
                              <Trash2 className="size-4" />
                              삭제
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
            </div>
          </div>
        </>
      ) : isWorkBookPreviewOpen ? (
            <div className="container mx-auto flex h-full flex-col items-center px-6 py-8">
              <div className="w-full max-w-5xl">
                <div className="mb-8 flex items-center justify-between">
                  <Button
                    variant="secondary"
                    onClick={() => setIsWorkBookPreviewOpen(false)}
                  >
                    닫기
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleWorkBookPrint}
                    className="flex items-center gap-2"
                  >
                    <Printer className="size-5" />
                    인쇄하기
                  </Button>
                </div>
                <div className="flex justify-center overflow-y-auto">
                  <WorkBookPrint
                    bookId={selectedBookId}
                    mode="preview"
                    scale={0.7}
                    onPrint={setWorkBookPrintFunction}
                    addEmptyPage={previewSettings.addEmptyPage}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="container mx-auto flex h-full flex-col items-center px-6 py-8">
              <div className="w-full max-w-5xl">
                <div className="mb-8 flex items-center justify-between">
                  <Button
                    variant="secondary"
                    onClick={() => setShowPreview(false)}
                  >
                    닫기
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handlePrint}
                    className="flex items-center gap-2"
                  >
                    <Printer className="size-5" />
                    인쇄하기
                  </Button>
                </div>
                <div className="flex justify-center overflow-y-auto">
                  <ErrorNotePrint
                    noteId={selectedNoteId}
                    mode="preview"
                    scale={0.7}
                    onPrint={setPrintFunction}
                    addEmptyPage={previewSettings.addEmptyPage}
                  />
                </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <ExamListModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          examList={examList}
          stampId={stamp_id}
          tree_name={stampInfo?.tree_name}
          edu_ver={stampInfo?.edu_ver}
          student_name={stampInfo?.student_name}
          onWrongAnswerGenerated={addWrongAnswerData}
        />
      )}

      <Modal
            isOpen={isWorkBookModalOpen}
            onClose={() => setIsWorkBookModalOpen(false)}
            title="문제집 생성"
            size="sm"
          >
            <form onSubmit={(e) => {
              e.preventDefault();
              const probsNumber = parseInt(e.target.probsNumber.value);
              handleCreateWorkBook(selectedReviewId, probsNumber);
            }}>
              <div className="mb-4">
                <label htmlFor="probsNumber" className="mb-2 block text-sm font-medium text-gray-700">
                  생성할 문제 수
                </label>
                <input
                  type="number"
                  id="probsNumber"
                  name="probsNumber"
                  min="1"
                  max="400"
                  defaultValue="200"
                  className="w-full rounded-3xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsWorkBookModalOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  생성
                </Button>
              </div>
            </form>
          </Modal>

          {/* 오답노트 미리보기 설정 모달 추가 */}
          <Modal
            isOpen={showPreviewSettings}
            onClose={() => setShowPreviewSettings(false)}
            title="오답노트 미리보기 설정"
            size="sm"
          >
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={previewSettings.addEmptyPage}
                  onChange={(e) => setPreviewSettings(prev => ({
                    ...prev,
                    addEmptyPage: e.target.checked
                  }))}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">연습장 추가</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowPreviewSettings(false)}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowPreviewSettings(false);
                  setShowPreview(true);
                }}
              >
                미리보기
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowPreviewSettings(false);
                  setShowV4Print(true);
                }}
              >
                신규 인쇄
              </Button>
            </div>
          </Modal>

          {/* 오답노트 신규 인쇄(v4) — 설정 모달의 「신규 인쇄」 버튼이 연다. 연습장 옵션은 구형 전용(1차 제외) */}
          <ErrnotePrintOverlay
            noteId={selectedNoteId}
            open={showV4Print && !!selectedNoteId}
            onClose={() => setShowV4Print(false)}
          />

          <Modal
            isOpen={isWorkBookPreviewModalOpen}
            onClose={() => setIsWorkBookPreviewModalOpen(false)}
            title="문제집 미리보기 설정"
            size="sm"
          >
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={previewSettings.addEmptyPage}
                  onChange={(e) => setPreviewSettings(prev => ({
                    ...prev,
                    addEmptyPage: e.target.checked
                  }))}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">연습장 추가</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsWorkBookPreviewModalOpen(false)}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={startWorkBookPreview}
              >
                미리보기
              </Button>
            </div>
          </Modal>

          {/* 메모 수정 모달 */}
          <Modal
            isOpen={memoModalOpen}
            onClose={() => setMemoModalOpen(false)}
            title="메모 수정"
            size="sm"
          >
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateMemo();
            }}>
              <div className="mb-4">
                <label htmlFor="memo" className="mb-2 block text-sm font-medium text-gray-700">
                  메모 내용 (최대 500자)
                </label>
                <textarea
                  id="memo"
                  value={editingMemo.memo}
                  onChange={(e) => setEditingMemo(prev => ({ ...prev, memo: e.target.value }))}
                  className="h-32 w-full resize-none rounded-3xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  maxLength={500}
                  placeholder="메모를 입력하세요..."
                />
                <div className="mt-1 text-right text-sm text-gray-500">
                  {editingMemo.memo.length}/500
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setMemoModalOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                >
                  저장
                </Button>
              </div>
            </form>
          </Modal>

          {/* 삭제 확인 모달 */}
          <Modal
            isOpen={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            title="오답노트 삭제 확인"
            size="sm"
          >
            <div className="mb-4">
              <p className="text-sm text-gray-700">
                정말로 이 오답노트를 삭제하시겠습니까?
              </p>
              <p className="mt-2 text-sm font-semibold text-red-600">
                ⚠️ 이 작업은 되돌릴 수 없습니다.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                오답노트와 관련된 문제집도 함께 삭제됩니다.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDeleteModalOpen(false)}
              >
                취소
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteReview}
              >
                삭제
              </Button>
            </div>
          </Modal>
    </Layout>
  );
}

export default WrongAnswer;
